import { useState } from 'react';

import { useChangeCommunitySubscriptionStatus } from '../../../../redux/api/communitiesApi';
import {
  useChangeUserSubscriptionStatus,
  useSubscribeToUser,
  useUnsubscribeFromUser,
} from '../../../../redux/api/users/usersApi';
import { RelationStatus } from '../../../../shared/constants';
import { IUser } from '../../../../types/interfaces/user';
import { useOptionalUsersListRefresh } from '../UsersListRefreshContext';

export type SubscriptionStatusIntent = 'approve' | 'reject' | 'drop';

export function useUserBigItem(user: IUser, communityId?: string) {
  const refreshUsersList = useOptionalUsersListRefresh();

  const [changeUserSubscriberStatus, { isLoading: isUserStatusLoading }] =
    useChangeUserSubscriptionStatus();
  const [
    changeCommunitySubscriberStatus,
    { isLoading: isCommunityStatusLoading },
  ] = useChangeCommunitySubscriptionStatus();
  const [subscribeToUser, { isLoading: isSubscribeLoading }] =
    useSubscribeToUser();
  const [unsubscribeFromUser, { isLoading: isUnsubscribeLoading }] =
    useUnsubscribeFromUser();

  const [pendingSubscribe, setPendingSubscribe] = useState<string | null>(null);
  const [pendingUnsubscribe, setPendingUnsubscribe] = useState<string | null>(
    null,
  );
  const [pendingStatus, setPendingStatus] = useState<{
    userId: string;
    intent: SubscriptionStatusIntent;
  } | null>(null);

  const statusLoading = communityId
    ? isCommunityStatusLoading
    : isUserStatusLoading;

  async function onSubscribe(userId: string) {
    setPendingSubscribe(userId);
    try {
      await subscribeToUser({ id: userId }).unwrap();
      if (!communityId) {
        void refreshUsersList?.();
      }
    } catch (e) {
      console.log(e);
    } finally {
      setPendingSubscribe(null);
    }
  }

  async function onUnsubscribe(userId: string) {
    setPendingUnsubscribe(userId);
    try {
      await unsubscribeFromUser({ id: userId }).unwrap();
      if (!communityId) {
        void refreshUsersList?.();
      }
    } catch (e) {
      console.log(e);
    } finally {
      setPendingUnsubscribe(null);
    }
  }

  async function onSubscriptionStatusChange(
    userId: string,
    status: RelationStatus,
    intent: SubscriptionStatusIntent,
  ) {
    setPendingStatus({ userId, intent });
    try {
      if (communityId) {
        await changeCommunitySubscriberStatus({
          id: communityId,
          body: { status, userId },
        }).unwrap();
      } else {
        await changeUserSubscriberStatus({
          id: userId,
          body: { status },
        }).unwrap();
        void refreshUsersList?.();
      }
    } catch (e) {
      console.log(e);
    } finally {
      setPendingStatus(null);
    }
  }

  const isThisUser = (id: string) => id === user.id;

  return {
    onSubscribe,
    onUnsubscribe,
    onSubscriptionStatusChange,
    isSubscribeLoading:
      isThisUser(pendingSubscribe ?? '') && isSubscribeLoading,
    isUnsubscribeLoading:
      isThisUser(pendingUnsubscribe ?? '') && isUnsubscribeLoading,
    isApproveStatusLoading:
      pendingStatus?.userId === user.id &&
      pendingStatus.intent === 'approve' &&
      statusLoading,
    isRejectStatusLoading:
      pendingStatus?.userId === user.id &&
      pendingStatus.intent === 'reject' &&
      statusLoading,
    isDropStatusLoading:
      pendingStatus?.userId === user.id &&
      pendingStatus.intent === 'drop' &&
      statusLoading,
  };
}
